import { inquiryCoordinationSchema } from '@inquiries/-lib/inquiryValidation'
import { useCallback, useState } from 'react'
import { validateForm } from '@/lib/form-validation'

type UseCoordinationItemEditParams = {
  initialContent: string
  onEdit: (content: string) => Promise<void>
  onEditingChange?: (isEditing: boolean) => void
}

export function useCoordinationItemEdit({ initialContent, onEdit, onEditingChange }: UseCoordinationItemEditParams) {
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(initialContent)
  const [editContentError, setEditContentError] = useState<string | undefined>()

  const handleEditStart = useCallback(() => {
    setEditContent(initialContent)
    setEditContentError(undefined)
    setIsEditing(true)
    onEditingChange?.(true)
  }, [initialContent, onEditingChange])

  const handleEditSave = useCallback(async () => {
    const { success, errors } = validateForm(inquiryCoordinationSchema, { content: editContent })
    if (!success) {
      setEditContentError(errors.content)
      return
    }
    setEditContentError(undefined)
    try {
      await onEdit(editContent.trim())
      setIsEditing(false)
      onEditingChange?.(false)
    } catch {}
  }, [editContent, onEdit, onEditingChange])

  const handleEditCancel = useCallback(() => {
    setEditContent(initialContent)
    setEditContentError(undefined)
    setIsEditing(false)
    onEditingChange?.(false)
  }, [initialContent, onEditingChange])

  const handleContentChange = useCallback((value: string) => {
    setEditContent(value)
    setEditContentError(undefined)
  }, [])

  return {
    isEditing,
    editContent,
    editContentError,
    handleEditStart,
    handleEditSave,
    handleEditCancel,
    handleContentChange,
  }
}
